import { useCallback, useContext } from 'react'

import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { InspectionItem, InspectionSheet } from '@editor/interfaces/inspection'
import { ERROR_PROCESS, processErrorHandler } from '@editor/services/ErrorHandler'
import { getInspectionItems, getInspectionSheets } from '@editor/services/InspectionSheet'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'

import {
  setInspectionItems,
  setInspectionSheet,
  setInspectionSheets,
  setLoading,
} from '../editor/store/inspectionItems'

/**
 * Custom hook for fetching inspection items.
 *
 * @param fetchAlignedAreas Whether to fetch items for all aligned areas.
 */
export const useInspectionItems = (fetchAlignedAreas = false) => {
  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useGlobalModalContext()

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const connectedAreas = useSelector((state: RootState) => state.page.connectedAreas)
  const inspectionSheet = useSelector((state: RootState) => state.inspectionItems.inspectionSheet)

  /**
   * Fetch the inspection sheet for the current project and inspection area.
   */
  const fetchInspectionSheet = useCallback(async () => {
    if (!project || !inspectionArea) {
      return false
    }

    const token = await getAccessToken()
    if (!token) {
      return false
    }

    const inspectionSheets = await getInspectionSheets(
      token,
      project.project_id,
      inspectionArea.inspection_area_id,
      showErrorModal,
    )

    if (!inspectionSheets.length || !inspectionSheets[0].inspection_sheet_id) {
      showErrorModal('帳票が存在しません。')
      return false
    }

    dispatch(setInspectionSheet(inspectionSheets[0]))

    return inspectionSheets[0]
  }, [project, inspectionArea, getAccessToken, showErrorModal, dispatch])

  /**
   * Actual fetching of inspection items.
   * This is separated from the exposed function to avoid listing inspectionSheet as a dependency
   * (which would cause infinite loops if called from useEffect).
   *
   * @param sheet Optional inspection sheet of the current area. If fetchAlignedAreas is false and this is not provided,
   *              the function will return false.
   */
  const fetchItems = useCallback(
    async (sheet?: InspectionSheet) => {
      if (!project || !inspectionArea) {
        return false
      }

      const token = await getAccessToken()
      if (!token) {
        return false
      }

      const items: InspectionItem[] = []

      try {
        // -- Fetch all items including aligned areas
        if (fetchAlignedAreas) {
          if (!connectedAreas || Object.keys(connectedAreas).length === 0) return false

          // Get all area IDs, including aligned areas
          const areaIds = connectedAreas.map(({ current: { area } }) => area.inspection_area_id)
          const results = await Promise.all(
            areaIds.map((areaId) =>
              getInspectionSheets(token, project.project_id, areaId).then(async (sheets) => {
                if (!sheets.length || !sheets[0].inspection_sheet_id) {
                  showErrorModal('帳票が存在しません。')
                  return { sheet: undefined, items: [] }
                }

                return {
                  areaId,
                  sheet: sheets[0],
                  items: await getInspectionItems(token, project.project_id, areaId, sheets[0].inspection_sheet_id),
                }
              }),
            ),
          )

          // Merge results from all areas
          items.push(...results.map(({ items: its }) => its).flat())

          // Store all fetched sheets
          // We'll need this later when patching inspection items.
          dispatch(
            setInspectionSheets(
              results.reduce(
                (acc, { areaId, sheet: sht }) => {
                  if (sht) {
                    acc[areaId] = sht
                  }
                  return acc
                },
                {} as Record<string, InspectionSheet>,
              ),
            ),
          )
        } else {
          if (!sheet) return false

          // -- Fetch items for the current area only
          items.push(
            ...(await getInspectionItems(
              token,
              project.project_id,
              inspectionArea.inspection_area_id,
              sheet.inspection_sheet_id,
              showErrorModal,
            )),
          )
        }

        dispatch(setInspectionItems(items))
        dispatch(setLoading(false))
        return items
      } catch (err) {
        processErrorHandler(err, ERROR_PROCESS.GET_INSPECTION_SHEET, showErrorModal)
        return false
      }
    },
    [project, inspectionArea, connectedAreas, fetchAlignedAreas, getAccessToken, showErrorModal, dispatch],
  )

  /**
   * Fetch inspection items for the current inspection area and sheet.
   */
  const fetchInspectionItems = useCallback(async () => {
    if (!inspectionSheet) return false
    return fetchItems(inspectionSheet)
  }, [inspectionSheet, fetchItems])

  /**
   * Fetch all inspection items and the inspection sheet.
   */
  const fetchAll = useCallback(async () => {
    if (!project || !inspectionArea) {
      return false
    }

    return fetchItems((fetchAlignedAreas ? undefined : await fetchInspectionSheet()) || undefined)
  }, [project, inspectionArea, fetchAlignedAreas, fetchItems, fetchInspectionSheet])

  return {
    fetchInspectionSheet,
    fetchInspectionItems,
    fetchAll,
  }
}
